import {
  RoadmapDependencyIdentitySelector,
  RoadmapDependencyPriorityBucket,
  RoadmapDependencyStatusBucket,
  RoadmapDependencyView,
} from 'client';

import type {RoadmapTab, RoadmapViewMode} from './types';

export type RoadmapAuditAssignee = 'all' | 'me';
export type RoadmapAuditRuleMode = 'any' | 'all';
export type RoadmapAudience = 'my' | 'person' | 'group' | 'company' | 'scope';

export interface RoadmapAuditFilters {
  assignee: RoadmapAuditAssignee;
  assigneeKeys: string[];
  ruleIds: string[];
  ruleMode: RoadmapAuditRuleMode;
  query: string | null;
}

export interface RoadmapDependencyFilters {
  includeBeforeQuarter: boolean;
  view: RoadmapDependencyView;
  statuses: RoadmapDependencyStatusBucket[];
  priorities: RoadmapDependencyPriorityBucket[];
  actionNeeded: boolean | null;
  assigned: RoadmapDependencyIdentitySelector;
  requested: RoadmapDependencyIdentitySelector;
  query: string | null;
}

export interface RoadmapSearchState {
  activeTab: RoadmapTab;
  audience: RoadmapAudience;
  hasExplicitAudienceSelection: boolean;
  requestedTimeframe: string | null;
  requestedScope: string | null;
  requestedPerson: string | null;
  requestedTeamId: string | null;
  rawAuditNarrativeKey: string | null;
  requestedQuarter: string | null;
  viewMode: RoadmapViewMode;
  auditFilters: RoadmapAuditFilters;
  dependencyFilters: RoadmapDependencyFilters;
}

export const DEFAULT_ROADMAP_AUDIT_FILTERS: RoadmapAuditFilters = {
  assignee: 'all',
  assigneeKeys: [],
  ruleIds: [],
  ruleMode: 'any',
  query: null,
};

export const DEFAULT_ROADMAP_DEPENDENCY_FILTERS: RoadmapDependencyFilters = {
  includeBeforeQuarter: false,
  view: RoadmapDependencyView.DELIVERING_GROUP,
  statuses: [],
  priorities: [],
  actionNeeded: null,
  assigned: RoadmapDependencyIdentitySelector.ALL,
  requested: RoadmapDependencyIdentitySelector.ALL,
  query: null,
};

export const resolveRoadmapAudience = ({
  requestedAudience,
  hasExplicitSelection,
  myRoadmapItemCount,
}: {
  requestedAudience: RoadmapAudience;
  hasExplicitSelection: boolean;
  myRoadmapItemCount: number | null;
}): RoadmapAudience => {
  if (requestedAudience !== 'my' || hasExplicitSelection || myRoadmapItemCount == null)
    return requestedAudience;
  return myRoadmapItemCount > 0 ? 'my' : 'company';
};

const ROADMAP_DEPENDENCY_VIEWS = Object.values(RoadmapDependencyView);
const ROADMAP_DEPENDENCY_STATUSES = Object.values(RoadmapDependencyStatusBucket);
const ROADMAP_DEPENDENCY_PRIORITIES = Object.values(RoadmapDependencyPriorityBucket);

const getNormalizedRepeatedValues = (
  searchParams: URLSearchParams,
  key: string,
  isValid: (value: string) => boolean
) => {
  const values = searchParams
    .getAll(key)
    .flatMap((value) => value.split(','))
    .map((value) => value.trim())
    .filter((value) => value.length > 0 && isValid(value));

  return [...new Set(values)];
};

export const getRoadmapAuditFilters = (searchParams: URLSearchParams): RoadmapAuditFilters => {
  const assignee = searchParams.get('auditAssignee') === 'me' ? 'me' : 'all';
  const assigneeKeys =
    assignee === 'me'
      ? []
      : getNormalizedRepeatedValues(
          searchParams,
          'auditAssigneeKey',
          (value) => value.length <= 255
        );
  const ruleIds = getNormalizedRepeatedValues(searchParams, 'auditRule', (value) =>
    /^[a-z][a-z0-9_]{0,63}$/.test(value)
  );
  const rawQuery = searchParams.get('auditQuery')?.trim() ?? '';

  return {
    assignee,
    assigneeKeys,
    ruleIds,
    ruleMode: searchParams.get('auditRuleMode') === 'all' ? 'all' : 'any',
    query: rawQuery.length > 0 && rawQuery.length <= 100 ? rawQuery : null,
  };
};

export const getRoadmapDependencyFilters = (
  searchParams: URLSearchParams
): RoadmapDependencyFilters => {
  const rawView = searchParams.get('dependencyView');
  const rawQuery = searchParams.get('dependencyQuery')?.trim() ?? '';
  const statuses = getNormalizedRepeatedValues(searchParams, 'dependencyStatus', (value) =>
    ROADMAP_DEPENDENCY_STATUSES.includes(value as RoadmapDependencyStatusBucket)
  ) as RoadmapDependencyStatusBucket[];
  const priorities = getNormalizedRepeatedValues(searchParams, 'dependencyPriority', (value) =>
    ROADMAP_DEPENDENCY_PRIORITIES.includes(value as RoadmapDependencyPriorityBucket)
  ) as RoadmapDependencyPriorityBucket[];

  return {
    includeBeforeQuarter: ['1', 'true'].includes(
      searchParams.get('dependencyBeforeQuarter')?.toLowerCase() ?? ''
    ),
    view: ROADMAP_DEPENDENCY_VIEWS.includes(rawView as RoadmapDependencyView)
      ? (rawView as RoadmapDependencyView)
      : RoadmapDependencyView.DELIVERING_GROUP,
    statuses: statuses.sort(
      (left, right) =>
        ROADMAP_DEPENDENCY_STATUSES.indexOf(left) - ROADMAP_DEPENDENCY_STATUSES.indexOf(right)
    ),
    priorities: priorities.sort(
      (left, right) =>
        ROADMAP_DEPENDENCY_PRIORITIES.indexOf(left) - ROADMAP_DEPENDENCY_PRIORITIES.indexOf(right)
    ),
    actionNeeded: ['1', 'true'].includes(
      searchParams.get('dependencyActionNeeded')?.toLowerCase() ?? ''
    )
      ? true
      : null,
    assigned:
      searchParams.get('dependencyAssigned') === RoadmapDependencyIdentitySelector.ME
        ? RoadmapDependencyIdentitySelector.ME
        : RoadmapDependencyIdentitySelector.ALL,
    requested:
      searchParams.get('dependencyRequested') === RoadmapDependencyIdentitySelector.ME
        ? RoadmapDependencyIdentitySelector.ME
        : RoadmapDependencyIdentitySelector.ALL,
    query: rawQuery.length > 0 && rawQuery.length <= 100 ? rawQuery : null,
  };
};

export const getRoadmapSearchState = (searchParams: URLSearchParams): RoadmapSearchState => {
  const rawTab = searchParams.get('tab');
  const requestedScope =
    searchParams.get('scope') ?? searchParams.get('group') ?? searchParams.get('narrative');
  const rawRequestedPerson = searchParams.get('person')?.trim() ?? '';
  const requestedPerson =
    rawRequestedPerson.length > 0 && rawRequestedPerson.length <= 255 ? rawRequestedPerson : null;
  const rawRequestedTeamId = searchParams.get('team')?.trim() ?? '';
  const requestedTeamId =
    rawRequestedTeamId.length > 0 &&
    rawRequestedTeamId.length <= 255 &&
    /^[A-Za-z0-9][A-Za-z0-9._:-]*$/.test(rawRequestedTeamId)
      ? rawRequestedTeamId
      : null;
  const rawAudience = searchParams.get('audience');
  const hasExplicitAudienceSelection = Boolean(
    requestedScope ||
    requestedPerson ||
    requestedTeamId ||
    rawAudience === 'company' ||
    rawAudience === 'my'
  );
  const audience: RoadmapAudience = requestedScope
    ? 'scope'
    : requestedPerson
      ? 'person'
      : requestedTeamId
        ? 'group'
        : rawAudience === 'my'
          ? 'my'
          : rawAudience === 'company'
            ? 'company'
            : 'company';
  const isPeopleScope = audience === 'my' || audience === 'person';

  const activeTab: RoadmapTab =
    rawTab === 'all' && isPeopleScope
      ? 'all'
      : rawTab === 'launches' ||
          rawTab === 'roadmap' ||
          rawTab === 'dependencies' ||
          rawTab === 'rollup' ||
          rawTab === 'allocations' ||
          rawTab === 'audit'
        ? (rawTab as RoadmapTab)
        : rawTab === 'dashboard' && isPeopleScope
          ? 'all'
          : rawTab === 'overview' || rawTab === 'dashboard'
            ? 'launches'
            : isPeopleScope
              ? 'all'
              : 'launches';

  return {
    activeTab,
    audience,
    hasExplicitAudienceSelection,
    requestedTimeframe: searchParams.get('timeframe'),
    requestedScope,
    requestedPerson,
    requestedTeamId,
    rawAuditNarrativeKey: searchParams.get('auditNarrative'),
    requestedQuarter: searchParams.get('quarter'),
    viewMode: searchParams.get('view') === 'gantt' ? 'gantt' : 'table',
    auditFilters: getRoadmapAuditFilters(searchParams),
    dependencyFilters: getRoadmapDependencyFilters(searchParams),
  };
};

export const buildRoadmapSearchParams = (
  current: URLSearchParams,
  updates: Record<string, string | string[] | null>
) => {
  const next = new URLSearchParams(current);

  Object.entries(updates).forEach(([key, value]) => {
    next.delete(key);
    if (!value) {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item) next.append(key, item);
      });
      return;
    }

    next.set(key, value);
  });

  return next;
};

export const buildRoadmapAuditSearchParams = (
  current: URLSearchParams,
  filters: RoadmapAuditFilters
) =>
  buildRoadmapSearchParams(current, {
    auditAssignee: filters.assignee === 'me' ? 'me' : null,
    auditAssigneeKey: filters.assignee === 'all' ? filters.assigneeKeys : null,
    auditRule: filters.ruleIds,
    auditRuleMode: filters.ruleMode === 'all' ? 'all' : null,
    auditQuery: filters.query,
  });

export const buildRoadmapDependencySearchParams = (
  current: URLSearchParams,
  filters: RoadmapDependencyFilters
) =>
  buildRoadmapSearchParams(current, {
    dependencyBeforeQuarter: filters.includeBeforeQuarter ? '1' : null,
    dependencyView: filters.view === RoadmapDependencyView.DELIVERING_GROUP ? null : filters.view,
    dependencyStatus: filters.statuses,
    dependencyPriority: filters.priorities,
    dependencyActionNeeded: filters.actionNeeded ? '1' : null,
    dependencyAssigned:
      filters.assigned === RoadmapDependencyIdentitySelector.ME ? filters.assigned : null,
    dependencyRequested:
      filters.requested === RoadmapDependencyIdentitySelector.ME ? filters.requested : null,
    dependencyQuery: filters.query,
  });

export const getActiveRoadmapDependencyFilterCount = (filters: RoadmapDependencyFilters) =>
  Number(filters.includeBeforeQuarter) +
  Number(filters.view !== RoadmapDependencyView.DELIVERING_GROUP) +
  filters.statuses.length +
  filters.priorities.length +
  Number(filters.actionNeeded === true) +
  Number(filters.assigned === RoadmapDependencyIdentitySelector.ME) +
  Number(filters.requested === RoadmapDependencyIdentitySelector.ME) +
  Number(filters.query !== null);

export const hasActiveRoadmapDependencyFilters = (filters: RoadmapDependencyFilters) =>
  getActiveRoadmapDependencyFilterCount(filters) > 0;
